import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateAddon, useAddonJobs, useSyncAddon } from '../../../hooks/useAddons'
import type { GithubAuthMode } from '../../../api/client'
import type { Addon } from '../../../api/client'
import { Alert, Badge, Button, Input, LinkButton, Modal, SegmentedControl } from '../../../components/ui'
import type { BadgeTone } from '../../../components/ui'
import { AuthPicker, DeployKeyPanel } from './GitHubInstall'
import { UploadProgress } from './bits'

// ── Job logs modal ────────────────────────────────────────────────────────────

const jobStatusTone: Record<string, BadgeTone> = {
  running: 'accent',
  success: 'success',
  failed: 'danger',
  rolled_back: 'danger',
}

export function JobLogsModal({ instanceId, addon, onClose }: { instanceId: string; addon: Addon; onClose: () => void }) {
  const { t } = useTranslation()
  const { data: jobs } = useAddonJobs(instanceId, addon.id, true)
  const latest = jobs?.[0]
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (latest?.logs) {
      navigator.clipboard.writeText(latest.logs)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Modal's `scrollable` caps the *panel* at 80vh and scrolls its whole body —
  // which would carry the job meta out of view with the log, and the body's own
  // padding means the ink surface can never bleed to the panel edge anyway. So
  // the panel stays auto-height and the log keeps its own capped scroll box.
  return (
    <Modal open onClose={onClose} size="xl" title={t('detail.jobLogs.title', { name: addon.name })}>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            {latest && (
              <Badge tone={jobStatusTone[latest.status] ?? 'neutral'}>
                {latest.action} · {latest.status}
                {latest.exit_code !== undefined && ` ${t('detail.jobLogs.exit', { code: latest.exit_code })}`}
              </Badge>
            )}
            {latest?.changed !== undefined && (
              // A successful upgrade says nothing about whether new code was
              // pulled first; spell that out rather than letting green read as
              // "deployed".
              <p className={`text-xs ${latest.changed ? 'text-green-700' : 'text-amber-700'}`}>
                {latest.changed
                  ? t('detail.jobLogs.codeChanged', {
                      ref: latest.source_ref ?? '',
                      commit: latest.pulled_commit ? latest.pulled_commit.slice(0, 7) : '-',
                    })
                  : t('detail.jobLogs.codeUnchanged', { ref: latest.source_ref ?? '' })}
              </p>
            )}
          </div>
          {latest?.logs && (
            <LinkButton tone="accent" className="shrink-0" onClick={handleCopy}>
              {copied ? t('common.copiedAll') : t('common.copyAll')}
            </LinkButton>
          )}
        </div>
        {/* ink-*, not gray-*: a log terminal is a deliberately dark surface. */}
        <div className="max-h-[60vh] overflow-auto bg-ink-900 rounded-lg p-4 font-mono text-xs text-ink-300 whitespace-pre-wrap">
          {!latest ? (
            <span className="text-ink-500">{t('detail.jobLogs.noJobs')}</span>
          ) : latest.logs ? (
            latest.logs
          ) : (
            <span className="text-ink-500">{latest.status === 'running' ? t('common.running') : t('common.noOutput')}</span>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ── Update modal ──────────────────────────────────────────────────────────────

// UpdateModal is the single entry point for updating an already-installed addon.
// Primary action is "pull latest from source" (sync). The modal also offers ZIP
// upload and "override source URL" as secondary options; the user rarely needs
// them but they stay out of the way until requested.
export function UpdateModal({ instanceId, addon, onClose }: { instanceId: string; addon: Addon; onClose: () => void }) {
  const { t } = useTranslation()
  const hasSource = !!addon.source_url
  const [tab, setTab] = useState<'source' | 'zip' | 'override'>(hasSource ? 'source' : 'zip')
  const [githubUrl, setGithubUrl] = useState(addon.source_url ?? '')
  const [ref, setRef] = useState('main')
  const [auth, setAuth] = useState<GithubAuthMode>('public')
  const [error, setError] = useState('')
  // When the repo/zip is multi-root and no module name matches the addon, the
  // backend returns 409 + a list of modules; we surface that as a picker.
  const [pendingPick, setPendingPick] = useState<{ modules: string[]; file?: File; githubUrl?: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const updateMutation = useUpdateAddon(instanceId)
  const syncMutation = useSyncAddon(instanceId)

  const runSync = () => {
    setError('')
    syncMutation.mutate(addon.id, {
      onSuccess: onClose,
      onError: (e: unknown) => {
        const d = (e as { response?: { status?: number; data?: { code?: string; error?: string } } })?.response
        if (d?.data?.code === 'deploy_key_required') {
          // Escalate: the stored source needs a deploy key — surface the override
          // tab so the user can pick the auth mode and the key panel appears.
          setTab('override')
          setAuth('deploy_key')
          setError(t('detail.addons.deployKeyNeeded'))
          return
        }
        setError(d?.data?.error ?? t('detail.addonsExtra.updateFailed'))
      },
    })
  }

  const runOverride = (file?: File, ghUrl?: string, moduleName?: string) => {
    setError('')
    updateMutation.mutate({ addonId: addon.id, file, githubUrl: ghUrl, ref, auth, moduleName }, {
      onSuccess: () => { setPendingPick(null); onClose() },
      onError: (e: unknown) => {
        const resp = (e as { response?: { status?: number; data?: { code?: string; error?: string; modules?: string[] } } })?.response
        const d = resp?.data
        if (d?.code === 'deploy_key_required') {
          setAuth('deploy_key')
          setError(t('detail.addons.deployKeyNeeded'))
          return
        }
        if (resp?.status === 409 && Array.isArray(d?.modules) && d!.modules!.length > 0) {
          setPendingPick({ modules: d!.modules!, file, githubUrl: ghUrl })
          setError('')
          return
        }
        setError(d?.error ?? t('detail.addonsExtra.updateFailed'))
      },
    })
  }

  // Backdrop click and Escape (both new, from Modal) go through the same abort
  // as the Cancel button — an in-flight upload must not outlive the dialog.
  const close = () => {
    if (updateMutation.isPending) updateMutation.abort()
    onClose()
  }

  const tabs: ('source' | 'override' | 'zip')[] = hasSource ? ['source', 'override', 'zip'] : ['zip', 'override']

  return (
    <Modal
      open
      onClose={close}
      size="md"
      title={t('detail.addons.updateTitle', { name: addon.name })}
      footer={
        <>
          <Button variant="secondary" onClick={close}>{t('detail.addons.updateCancel')}</Button>
          {tab === 'source' && hasSource && (
            <Button onClick={runSync} loading={syncMutation.isPending}>
              {syncMutation.isPending ? t('detail.addons.syncing') : t('detail.addons.updateRunSync')}
            </Button>
          )}
          {tab === 'override' && (
            <Button onClick={() => runOverride(undefined, githubUrl)} loading={updateMutation.isPending} disabled={!githubUrl}>
              {updateMutation.isPending ? t('detail.addons.updateRunning') : t('detail.addons.updateRun')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <SegmentedControl
          size="sm"
          value={tab}
          onChange={setTab}
          label={t('detail.addons.updateSourceLabel', { defaultValue: 'Update source' })}
          options={tabs.map((tabKey) => ({
            value: tabKey,
            label: tabKey === 'source' ? t('detail.addons.updateTabSync') : tabKey === 'zip' ? t('detail.addons.updateTabZip') : t('detail.addons.updateTabOverride'),
          }))}
        />

        {tab === 'source' && hasSource && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: t('detail.addons.updateSyncHint', { url: addon.source_url }) }} />
            <p className="text-xs text-gray-400">{t('detail.addons.updateSyncAuthNote')}</p>
          </div>
        )}
        {tab === 'zip' && (
          <div className="space-y-3">
            <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) runOverride(f) }} />
            {/* A dashed drop-target affordance, not a filled action — left off Button on purpose. */}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={updateMutation.isPending}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed">
              {t('detail.addons.updateClickZip')}
            </button>
            {updateMutation.isPending && (
              <UploadProgress
                progress={updateMutation.progress}
                uploadingLabel={t('detail.addons.uploadingPercent', { pct: updateMutation.progress })}
                processingLabel={t('detail.addons.updateRunning')}
              />
            )}
          </div>
        )}
        {tab === 'override' && (
          <div className="space-y-3">
            <Input
              type="text"
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value)}
              placeholder="https://github.com/owner/repo"
              aria-label={t('detail.addons.tabGithub')}
            />
            <Input
              type="text"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={t('detail.addons.branchPlaceholder')}
              aria-label={t('detail.addons.branchLabel', { defaultValue: 'Branch' })}
            />
            <AuthPicker auth={auth} setAuth={setAuth} />
            {auth === 'deploy_key' && <DeployKeyPanel repoUrl={githubUrl} />}
          </div>
        )}

        {pendingPick && (
          <Alert tone="warning">
            <p className="text-sm text-amber-900">{t('detail.addons.updatePickModule', { name: addon.name })}</p>
            <div className="space-y-1 max-h-48 overflow-y-auto mt-2">
              {pendingPick.modules.map((m) => (
                <button key={m} type="button"
                  onClick={() => runOverride(pendingPick.file, pendingPick.githubUrl, m)}
                  disabled={updateMutation.isPending}
                  className="w-full text-left text-sm px-2 py-1.5 rounded border border-amber-200 bg-white hover:bg-amber-100 disabled:opacity-50">
                  {m}
                </button>
              ))}
            </div>
            <LinkButton tone="neutral" className="mt-2" onClick={() => setPendingPick(null)}>
              {t('detail.addons.updatePickCancel')}
            </LinkButton>
          </Alert>
        )}

        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Modal>
  )
}
